import React, { useEffect, useRef, useState } from "react";
import VesselPositionImg from "../Media/vessel_position.svg";
import UserVesselPositionImg from "../Media/user_vessel_position.svg";
import { Button } from "reactstrap";
import { MapContainer, TileLayer, Marker, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

/* ------------------------------
   MAP PROVIDER CONFIG - MAPPLS (MapmyIndia)
   India-compliant map tiles
-------------------------------- */
const MAPPLS_API_KEY = process.env.REACT_APP_MAPPLS_API_KEY || "0ea918c36c5d42bcb0b6a4060b0cabd0";
const TILE_URL = `https://apis.mappls.com/advancedmaps/v1/${MAPPLS_API_KEY}/still_map/{z}/{x}/{y}.png`;
const TILE_ATTRIBUTION = '&copy; <a href="https://www.mappls.com/">Mappls</a>';

// Track tile errors - if too many, key may be expired
let tileErrorCount = 0;
const handleTileError = () => {
  tileErrorCount++;
  if (tileErrorCount === 5) {
    console.error("⚠️ MAPPLS API KEY MAY BE EXPIRED - Multiple tile load failures detected!");
    console.error("Please check: https://apis.mappls.com/console/");
  }
};

// Component to force map resize whenever container dimensions change
const MapResizer: React.FC = () => {
  const map = useMap();
  useEffect(() => {
    // Invalidate immediately + staggered delays for initial layout
    map.invalidateSize();
    const timers = [100, 300, 500, 1000, 2000].map((ms) =>
      setTimeout(() => map.invalidateSize(), ms)
    );

    // ResizeObserver catches layout shifts after initial mount
    const container = map.getContainer();
    let ro: ResizeObserver | null = null;
    if (container && typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(() => {
        map.invalidateSize();
      });
      ro.observe(container);
    }

    return () => {
      timers.forEach(clearTimeout);
      ro?.disconnect();
    };
  }, [map]);
  return null;
};

const VesselPositionMapStructure = ({
  setPopup,
  isPopup,
  imgSize,
  VesselDetailForMap,
}: any) => {
  const [hoveredVessel, setHoveredVessel] = useState<any>(null);
  const [cursorPos, setCursorPos] = useState({ x: 0, y: 0 });

  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);

  /** Prevent scroll when small map (non-popup) */
  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;

    const onWheel = (e: WheelEvent) => {
      if (!isPopup && wrapper.contains(e.target as Node)) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    window.addEventListener("wheel", onWheel, { passive: false, capture: true });
    return () => {
      window.removeEventListener("wheel", onWheel, { capture: true } as any);
    };
  }, [isPopup]);

  /** Fix tile layout when popup opens or screen resizes */
  useEffect(() => {
    const invalidate = () => {
      if (mapRef.current) {
        mapRef.current.invalidateSize();
        setTimeout(() => mapRef.current?.invalidateSize(), 200);
      }
    };

    invalidate();
    // Always run delayed invalidate for both popup and inline modes
    const timeout = setTimeout(invalidate, 50);

    const onResize = () => invalidate();
    window.addEventListener("resize", onResize);

    return () => {
      clearTimeout(timeout);
      window.removeEventListener("resize", onResize);
    };
  }, [isPopup]);

  /** Open popup */
  const handleWrapperClick = (e: React.MouseEvent) => {
    if (!isPopup) {
      e.stopPropagation();
      setPopup(true);
    }
  };

  return (
    <React.Fragment>
      {/* Zoom buttons in popup */}
      {isPopup && (
        <div
          className="d-flex gap-2"
          style={{
            zIndex: 1200,
            position: "absolute",
            right: 20,
            top: 20,
          }}
        >
          <Button color="primary" size="sm" onClick={() => mapRef.current?.zoomIn()}>
            <i className="bx bx-zoom-in font-16" />
          </Button>

          <Button color="primary" size="sm" onClick={() => mapRef.current?.zoomOut()}>
            <i className="bx bx-zoom-out font-16" />
          </Button>
        </div>
      )}

      {/* MAP WRAPPER */}
      <div
        ref={wrapperRef}
        id="map_wrapper"
        onClick={handleWrapperClick}
        style={{
          width: "100%",
          height: isPopup ? "70vh" : "250px",
          position: "relative",
          touchAction: "none",
          cursor: !isPopup ? "pointer" : "grab",
          borderRadius: isPopup ? "4px" : "0",
          overflow: "hidden",
        }}
      >
        <MapContainer
          ref={(mapInstance) => {
            mapRef.current = mapInstance as any;
          }}
          center={[20, 0]}
          zoom={isPopup ? 2 : 2}
          minZoom={2}
          maxZoom={isPopup ? 18 : 4}
          zoomControl={false}
          worldCopyJump={false}
          style={{ height: "100%", width: "100%" }}
          dragging={!!isPopup}
          scrollWheelZoom={!!isPopup}
          doubleClickZoom={!!isPopup}
          touchZoom={!!isPopup}
          attributionControl={false}
        >
          <MapResizer />
          <TileLayer
            url={TILE_URL}
            attribution={TILE_ATTRIBUTION}
            eventHandlers={{
              tileerror: () => handleTileError()
            }}
          />

          {Array.isArray(VesselDetailForMap) &&
            VesselDetailForMap.map((v: any, index: number) => {
              const icon = L.icon({
                iconUrl: v.user_assigned
                  ? UserVesselPositionImg
                  : VesselPositionImg,
                iconSize: [imgSize, imgSize],
                iconAnchor: [imgSize / 2, imgSize / 2],
              });

              return (
                <Marker
                  key={index}
                  position={[v.latitude, v.longitude]}
                  icon={icon}
                  eventHandlers={{
                    mouseover: () => {
                      setHoveredVessel(v);
                    },
                    mousemove: (e: any) => {
                      setCursorPos({
                        x: e.originalEvent.clientX,
                        y: e.originalEvent.clientY,
                      });
                    },
                    mouseout: () => {
                      setHoveredVessel(null);
                    },
                  }}
                />
              );
            })}
        </MapContainer>
      </div>

      {/* FLOATING TOOLTIP */}
      {hoveredVessel && (
        <div
          style={{
            position: "fixed",
            top: cursorPos.y + 15,
            left: cursorPos.x + 15,
            background: "rgba(31, 45, 65, 0.95)",
            color: "white",
            padding: "12px 14px",
            borderRadius: "6px",
            zIndex: 5000,
            pointerEvents: "none",
            width: "250px",
            boxShadow: "0 4px 15px rgba(0,0,0,0.25)",
            fontSize: "13px",
            lineHeight: "1.4",
            transition: "opacity 0.1s ease-out",
          }}
        >
          <div><strong>Vessel :</strong> {hoveredVessel.vessel_name}</div>
          <div><strong>Report Date-Time :</strong> {hoveredVessel.local_time ?? "-"}</div>
          <div><strong>Arrival Port :</strong> {hoveredVessel.arrival_port_name ?? "-"}</div>
          <div><strong>Departure Port :</strong> {hoveredVessel.departure_port_name ?? "-"}</div>
          <div><strong>Latitude :</strong> {hoveredVessel.latitude}</div>
          <div><strong>Longitude :</strong> {hoveredVessel.longitude}</div>
        </div>
      )}
    </React.Fragment>
  );
};

export default VesselPositionMapStructure;
